import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import * as IntegratePage from '../integrate/';
export const _frontmatter = {
  "title": "Mocking GraphQL API",
  "order": 23
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Action = makeShortcode("Action");
const Hint = makeShortcode("Hint");
const PageLink = makeShortcode("PageLink");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2 {...{
      "id": "pre-requisites"
    }}>{`Pre-requisites`}</h2>
    <p>{`Since we will be working with a GraphQL API, we need to have a GraphQL client installed and configured in our application. We need that client primarily to dispatch queries and mutations. Please refer to the getting started steps of the respective client.`}</p>
    <Action mdxType="Action">Install and configure a GraphQL client</Action>
    <p>{`Here are some GraphQL clients to consider:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.apollographql.com/"
        }}>{`Apollo`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://formidable.com/open-source/urql/"
        }}>{`URQL`}</a></li>
    </ul>
    <h3 {...{
      "id": "supported-clients"
    }}>{`Supported clients`}</h3>
    <p>{`Any GraphQL client that complies with the GraphQL operations specification can be used with Mock Service Worker. A client must dispatch requests with the following signature:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`interface GraphQLRequest {
  query: string
  variables?: Record<string, any>
}
`}</code></pre>
    <Hint mdxType="Hint">
  Some GraphQL clients append extra information to the request's payload. That
  information will be available in the response resolver function.
    </Hint>
    <h2 {...{
      "id": "imports"
    }}>{`Imports`}</h2>
    <p>{`In our `}<inlineCode parentName="p">{`src/mocks/handlers.js`}</inlineCode>{` file let's import the essentials we need for mocking a GraphQL API. They are grouped under the `}<inlineCode parentName="p">{`graphql`}</inlineCode>{` namespace exposed by the library.`}</p>
    <Action mdxType="Action">
  Import <code>graphql</code> from the <code>msw</code> package:
    </Action>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "showLineNumbers",
        "showLineNumbers": true
      }}>{`// src/mocks/handlers.js
import { graphql } from 'msw'
`}</code></pre>
    <h2 {...{
      "id": "request-handler"
    }}>{`Request handler`}</h2>
    <p>{`To handle a GraphQL request we need to specify its `}<em parentName="p">{`operation kind`}</em>{` (query/mutation).`}</p>
    <p>{`In this tutorial we will be mocking a basic login flow for our user. This flow implies handling two operations:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`Login`}</inlineCode>{` mutation, to allow a user to log in,`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`GetUserInfo`}</inlineCode>{` query, to return the information about the logged in user.`}</li>
    </ul>
    <Action mdxType="Action">
  Create request handlers by calling <code>graphql[OPERATION_KIND]</code> and
  providing an operation name:
    </Action>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "showLineNumbers focusedLines=6,9",
        "showLineNumbers": true,
        "focusedLines": "6,9"
      }}>{`// src/mocks/handlers.js
import { graphql } from 'msw'

export const handlers = [
  // Handles a "Login" mutation
  graphql.mutation('Login', null),

  // Handles a "GetUserInfo" query
  graphql.query('GetUserInfo', null),
]
`}</code></pre>
    <h2 {...{
      "id": "response-resolver"
    }}>{`Response resolver`}</h2>
    <p>{`To respond to an operation using a mocked response we have to specify it using a response resolver function.`}</p>
    <p>{`Response resolver is a function that accepts the following arguments:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`req`}</inlineCode>{`, an information about a matching request;`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`res`}</inlineCode>{`, a functional utility to create the mocked response;`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`ctx`}</inlineCode>{`, a group of functions that help to set a status code, headers, data, etc. on the mocked response.`}</li>
    </ul>
    <p>{`In GraphQL we describe the expected response in a query/mutation declaration itself. Let's design the shape of our response for both operations:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`# Authenticates using a given username
mutation Login($username: String!) {
  login(username: $username) {
    username
  }
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`# Returns the information about the authenticated user
query GetUserInfo() {
  user {
    username
    firstName
  }
}
`}</code></pre>
    <Action mdxType="Action">
  Provide response resolvers to the previously defined request handlers:
    </Action>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "showLineNumbers focusedLines=10-16,25-30,34-41",
        "showLineNumbers": true,
        "focusedLines": "10-16,25-30,34-41"
      }}>{`// src/mocks/handlers.js
import { graphql } from 'msw'

export const handlers = [
  // Handles a "Login" mutation
  graphql.mutation('Login', (req, res, ctx) => {
    const { username } = req.variables
    sessionStorage.setItem('is-authenticated', username)

    return res(
      ctx.data({
        login: {
          username,
        },
      }),
    )
  }),

  // Handles a "GetUserInfo" query
  graphql.query('GetUserInfo', (req, res, ctx) => {
    const authenticatedUser = sessionStorage.getItem('is-authenticated')

    if (!authenticatedUser) {
      // When not authenticated, respond with an error
      return res(
        ctx.errors([
          {
            message: 'Not authenticated',
            errorType: 'AuthenticationError',
          },
        ]),
      )
    }

    // When authenticated, respond with a query payload
    return res(
      ctx.data({
        user: {
          username: authenticatedUser,
          firstName: 'John',
        },
      }),
    )
  }),
]
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`Utilize things like `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage"
        }}><inlineCode parentName="a">{`sessionStorage`}</inlineCode></a>{`, `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage"
        }}><inlineCode parentName="a">{`localStorage`}</inlineCode></a>{`, or `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API"
        }}>{`IndexedDB`}</a>{` to handle more complex API scenarios and user interactions.`}</p>
    </blockquote>
    <Hint mode="warning" mdxType="Hint">
  <p>
    Some GraphQL clients (i.e.{' '}
    <a href="https://www.apollographql.com/docs/">Apollo</a>) may require you to
    include the <code>__typename</code> property on each individual type on the
    mocked response:
  </p>
  <code className="language-javascript" children={`
ctx.data({
  user: {
    firstName: 'John',
    lastName: 'Maverick',
    __typename: 'User'
  }
})
  `} />
  <p>
    Follow the response shape of your GraphQL client to ensure you produce a
    compatible mocked response.
  </p>
    </Hint>
    <h2 {...{
      "id": "next-step"
    }}>{`Next step`}</h2>
    <p>{`The request handlers are complete, yet there is one last step to perform: integrate the mocking.`}</p>

    <PageLink page={IntegratePage} url="/docs/getting-started/integrate" mdxType="PageLink" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      